/**
 * 3-Tier token registry — Semantic/Component assignment SSOT
 */

export interface TokenRefOption {
  token: string;
  label: string;
}

export const SEMANTIC_TOKEN_KEYS = [
  '--color-accent',
  '--color-surface',
  '--color-on-surface',
  '--color-primary',
  '--section-gap-sm',
  '--section-gap-md',
  '--section-gap-lg',
  '--section-stack-gap',
  '--font-display',
  '--font-body',
  '--button-bg',
  '--card-radius',
] as const;

export const COMPONENT_TOKEN_KEYS = [
  '--button-bg',
  '--button-radius',
  '--button-color',
  '--card-bg',
  '--card-radius',
  '--card-padding',
  '--section-title-size',
  '--section-subtitle-size',
  '--section-eyebrow-size',
] as const;

const PRIMITIVE_COLOR_REFS: TokenRefOption[] = [
  { token: '--color-brand-primary', label: 'Brand Primary' },
  { token: '--color-brand-secondary', label: 'Brand Secondary' },
  { token: '--color-brand-surface', label: 'Brand Surface' },
  { token: '--color-brand-on-surface', label: 'Brand On Surface' },
  { token: '--color-brand-accent', label: 'Brand Accent' },
];

const SEMANTIC_COLOR_REFS: TokenRefOption[] = [
  { token: '--color-accent', label: 'Accent' },
  { token: '--color-surface', label: 'Surface' },
  { token: '--color-on-surface', label: 'On Surface' },
  { token: '--color-primary', label: 'Primary' },
  { token: '--button-bg', label: 'Button BG (semantic)' },
];

const SPACING_REFS: TokenRefOption[] = [
  { token: '--section-gap-sm', label: 'Gap SM' },
  { token: '--section-gap-md', label: 'Gap MD' },
  { token: '--section-gap-lg', label: 'Gap LG' },
  { token: '--section-stack-gap', label: 'Stack gap' },
];

export function semanticRefOptionsForKey(key: string): TokenRefOption[] {
  if (key.startsWith('--font-')) {
    return [
      { token: '--font-display', label: 'Display font (primitive)' },
      { token: '--font-body', label: 'Body font (primitive)' },
    ];
  }
  if (key.includes('gap') || key.includes('padding')) {
    return SPACING_REFS;
  }
  if (key.includes('radius')) {
    return [
      { token: '0.25rem', label: '0.25rem' },
      { token: '0.5rem', label: '0.5rem' },
      { token: '0.75rem', label: '0.75rem' },
      { token: '1rem', label: '1rem' },
    ];
  }
  return PRIMITIVE_COLOR_REFS;
}

export function componentRefOptionsForKey(key: string): TokenRefOption[] {
  if (key.includes('radius') || key.includes('size')) {
    return [
      { token: 'var(--card-radius)', label: 'Card radius' },
      { token: '0.375rem', label: '0.375rem' },
      { token: '0.5rem', label: '0.5rem' },
      { token: '1.25rem', label: '1.25rem' },
      { token: '1.5rem', label: '1.5rem' },
    ];
  }
  if (key.includes('padding')) {
    return [
      { token: 'var(--section-gap-md)', label: 'Gap MD' },
      { token: 'var(--section-gap-sm)', label: 'Gap SM' },
      { token: 'var(--section-gap-lg)', label: 'Gap LG' },
    ];
  }
  return SEMANTIC_COLOR_REFS;
}

export function componentFromSemantic(semantic: Record<string, string>): Record<string, string> {
  return {
    '--button-bg': semantic['--button-bg'] ?? 'var(--color-brand-primary)',
    '--button-radius': '0.375rem',
    '--button-color': 'var(--color-on-surface)',
    '--card-bg': 'var(--color-surface)',
    '--card-radius': semantic['--card-radius'] ?? '0.5rem',
    '--card-padding': 'var(--section-gap-md)',
    '--section-title-size': 'clamp(1.5rem, 3vw, 2rem)',
    '--section-subtitle-size': '1rem',
    '--section-eyebrow-size': '0.75rem',
  };
}

export function wrapVarRef(token: string): string {
  if (
    token.startsWith('var(') ||
    token.startsWith('"') ||
    token.includes('rem') ||
    token.includes('calc')
  ) {
    return token;
  }
  return `var(${token})`;
}
